import React from 'react';
import { HelpTip, Icon, Text } from '@amzn/storm-ui-v3';
import { useTranslation } from 'react-i18next';
import { SINGLE_BRACKET_INTERPOLATION_OPTION } from '@urc-ui/utils/i18n-constants';
import { StyledCellWrapper, StyledLink } from '@urc-ui/components/styles';
import { spinner } from '@amzn/storm-ui-icons-v3';
import styled from 'styled-components';

const StyledText = styled(Text)`
  text-overflow: ellipsis;
  overflow: hidden;
`;

export interface LinkItem {
  // The text to display for the link
  readonly text: string;

  // The url to link to
  readonly url?: string;
}

interface PopoverListCellRendererProps {
  value: {
    // List of items and links to render on the table
    items: string[] | LinkItem[] | null;

    // The number of items to render before truncating and show 'XX more'
    renderLimit?: number;

    // Whether the list is loading
    isLoading?: boolean;

    // string id of the plurals text when there are > 1 element
    pluralsStringId: string;

    // fallback in case the plurals string can't be loaded
    defaultPluralsString: string;

    // optional click event handler
    onClickEvent?: () => void;
  };
}

/**
 * URC datatable renderer for a list of values. If there are no values, show a dash. If there is 1 value, this component
 * simply renders the value. If it's a list, the component renders the count with a help tip popover that lists all the
 * values. If the total item number is greater than max items to render, this component will truncate the list and show
 * 'XX more'. The format of the count text should have an interpolation option like `{0}`. Examples:
 *   {0} countries
 *   {0} recipients
 *   This foo has {0} bars
 * @param value See types definition
 */
const PopoverListCellRenderer = ({ value }: PopoverListCellRendererProps) => {
  const { items, renderLimit, isLoading, pluralsStringId, defaultPluralsString, onClickEvent } = value;
  const { t: translate } = useTranslation();

  if (isLoading) {
    return (
      <StyledCellWrapper>
        <Icon type={spinner} />
        <Text inline={true} fontSize='small'>
          {` ${translate('sspa_rcm_ad_reports_loading', 'Loading')}`}
        </Text>
      </StyledCellWrapper>
    );
  }

  if (!items || items.length === 0) {
    return (
      <StyledCellWrapper>
        <Text fontSize='small'>&#8212;</Text>
      </StyledCellWrapper>
    );
  }

  if (items && items.length === 1) {
    return (
      <StyledCellWrapper>
        {typeof items[0] === 'string' ? (
          <StyledText title={items[0]} fontSize='small'>
            {items[0]}
          </StyledText>
        ) : items[0].url ? (
          <StyledLink to={items[0].url} onClick={onClickEvent}>
            <StyledText title={items[0].text} fontSize='small'>
              {items[0].text}
            </StyledText>
          </StyledLink>
        ) : (
          <StyledText title={items[0].text} fontSize='small'>
            {items[0].text}
          </StyledText>
        )}
      </StyledCellWrapper>
    );
  }

  const isTruncationRequired = renderLimit && items.length > renderLimit;
  const itemsToRender = isTruncationRequired ? items.slice(0, renderLimit) : items;

  return (
    <StyledCellWrapper>
      <Text fontSize='small' type='span'>
        {translate(pluralsStringId, defaultPluralsString, {
          0: items.length,
          interpolation: SINGLE_BRACKET_INTERPOLATION_OPTION
        })}
        <HelpTip>
          {itemsToRender.map(item =>
            typeof item === 'string' ? (
              <Text key={item}>{item}</Text>
            ) : item.url ? (
              <StyledLink key={item.text} to={item.url} onClick={onClickEvent}>
                <Text>{item.text}</Text>
              </StyledLink>
            ) : (
              <Text key={item.text}>{item.text}</Text>
            )
          )}
          {isTruncationRequired && (
            <Text>
              {items.length - renderLimit} {translate('flexible_reports_more', 'more')}
            </Text>
          )}
        </HelpTip>
      </Text>
    </StyledCellWrapper>
  );
};

export { PopoverListCellRenderer };
